import { compareValues, type SortDir } from '../utils/tableSort';
import type { BillRunRow } from './billRunRows';
import { displayRow, type BillRunDisplay } from './billRunsDisplay';

// Bill Runs column sorting. Sorts on the values the table shows (displayRow),
// so what you click is what gets ordered. Same comparison as the other BV
// lists (utils/tableSort): blanks last whichever way, "Site 10" after "Site 2";
// ties keep the incoming (default) order, since Array.sort is stable.

export type BillRunsSortKey =
  | 'nmi' | 'entity' | 'period' | 'kwh' | 'retailer' | 'tariff'
  | 'expected' | 'actual' | 'variance' | 'status' | 'details' | 'assigned' | 'send';
export type BillRunsSort = { key: BillRunsSortKey; dir: SortDir };

export const BILL_RUNS_SORT_KEYS: BillRunsSortKey[] = [
  'nmi', 'entity', 'period', 'kwh', 'retailer', 'tariff',
  'expected', 'actual', 'variance', 'status', 'details', 'assigned', 'send',
];

/** Number columns: first click shows highest first. */
const NUMERIC = new Set<BillRunsSortKey>(['kwh', 'expected', 'actual', 'variance']);

const VALUE: Record<BillRunsSortKey, (d: BillRunDisplay) => string | number | null> = {
  nmi: (d) => d.nmi,
  entity: (d) => d.entity,
  // A missing row has no period; its month stands in, so it sits among its month's bills.
  period: (d) => d.periodFrom ?? `${d.month}-01`,
  kwh: (d) => d.expectedKwh,
  retailer: (d) => d.retailer,
  tariff: (d) => d.tariff,
  expected: (d) => d.expected,
  actual: (d) => d.actual,
  variance: (d) => d.variance,
  status: (d) => d.statusLabel,
  details: (d) => d.detailLabel,
  assigned: (d) => d.assignedLabel,
  send: (d) => d.sendLabel,
};

/** The sort after clicking `key`: first direction, then the other, then off (default order). */
export function nextSort(current: BillRunsSort | null, key: BillRunsSortKey): BillRunsSort | null {
  const first: SortDir = NUMERIC.has(key) ? 'desc' : 'asc';
  if (!current || current.key !== key) return { key, dir: first };
  if (current.dir === first) return { key, dir: first === 'asc' ? 'desc' : 'asc' };
  return null;
}

export function sortBillRunRows(rows: readonly BillRunRow[], sort: BillRunsSort | null): BillRunRow[] {
  if (!sort) return [...rows];
  const read = VALUE[sort.key];
  // displayRow once per row, not once per comparison.
  return rows
    .map((row) => ({ row, v: read(displayRow(row)) }))
    .sort((a, b) => compareValues(a.v, b.v, sort.dir))
    .map((x) => x.row);
}
